import { ActionCreatorWithPayload, configureStore, Middleware, AnyAction } from "@reduxjs/toolkit"
import { setupListeners } from "@reduxjs/toolkit/query"
import { set, throttleAsync } from "nodash"
import rootReducer, { RootState } from "./rootReducer"
import { getDebugWindow, isLocalDevelopmentEnvironment } from "tools/debugging/module"
import { addReporter } from "@nova/use-debug"
import socketio, { Socket } from "socket.io-client"
import { getSocketMiddleware } from "features/pubsub/sockets"
import { SocketHandler } from "features/pubsub/sockets/types"
import { selectWatching } from "features/pubsub/slice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { loadProject } from "features/map/control/thunks"
import { selectProjectHasLoaded } from "features/entity-caching/project/module"
import { firePerimeterListenerMiddleware } from "plugins/DrawPerimeter/FirePerimeterSlice"
import { flightListenerMiddleware } from "plugins/Video/controllers/hooks/flightListenerMiddleware"

let socketMiddleware: Middleware | null = null
let socketHandler: SocketHandler | null = null

export let socket: Socket | undefined

const createWebsocketMiddleware = () => {
    if (socketMiddleware) return socketMiddleware
    if (typeof window === "undefined" && !process.env.VITEST) {
        return (() => (next) => (action) => {
            next(action)
        }) as Middleware
    }
    socket = socketio("", {
        withCredentials: true,
        transports: ["websocket"],
        path: "/api/socket",
        reconnectionDelay: 2000,
        timeout: 5000,
    })

    let hasDisconnected = false

    const reloadProject = throttleAsync(
        async () => {
            hasDisconnected = false
            const projectId = selectActivatedFireId(store.getState())
            if (
                projectId &&
                socket?.auth &&
                "projectId" in socket.auth &&
                socket.auth.projectId === projectId
            ) {
                await store
                    .dispatch(
                        loadProject({
                            projectId,
                            orgId: selectActiveWorkspaceId()(store.getState())!,
                        })
                    )
                    .unwrap()
            }
        },
        5000,
        1
    )

    socket.on("disconnect", () => {
        const loaded = selectProjectHasLoaded(store.getState())
        if (loaded && "orgId" in (socket?.auth || {})) hasDisconnected = true
    })

    socket.on("connect", () => {
        if (hasDisconnected) {
            reloadProject()
        }
    })
    socketMiddleware = ((store) => (next) => (action: AnyAction) => {
        if (!socketHandler && socket) {
            socketHandler = getSocketMiddleware(store, socket)
        }
        if (action.type === "fires/loadProject/pending") {
            action.meta.arg.socket = socket
            return next(action)
        } else if (socket && socket.connected && socketHandler) socketHandler(action)

        const watching = selectWatching()(store.getState())
        if (!watching || action.type.startsWith("pubsub") || action.type.startsWith("project"))
            next(action)
    }) as Middleware
    return socketMiddleware
}
const actionsQueue = [] as unknown[]
const debugMiddleware = (() => (next) => (action: AnyAction) => {
    if (!getDebugWindow().printActions) {
        getDebugWindow().printActions = () => console.log(actionsQueue.toReversed())
    }
    if (action.type === getDebugWindow().traceAction) {
        console.log(new Error().stack)
    }
    if (getDebugWindow().traceActions) {
        console.log(action.type)
    }
    if (actionsQueue.length >= 50) actionsQueue.shift()
    actionsQueue.push(action)
    next(action)
}) as Middleware

export const createStore = (preloadedState: RootState = {} as RootState) =>
    configureStore({
        reducer: rootReducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({ serializableCheck: false })
                .concat(createWebsocketMiddleware())
                .concat(debugMiddleware)
                .prepend(firePerimeterListenerMiddleware.middleware)
                .prepend(flightListenerMiddleware.middleware),
        preloadedState,
    })

const store = createStore()
if (typeof window !== "undefined") {
    set(window, "store", store)
}

// Infer the `RootState` and `AppDispatch` types from the store itself
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch

export type Selector<T> = () => (s: RootState) => T
export type Action<T> = ActionCreatorWithPayload<T, string>

// development helper to access store from browser console
if (typeof window !== "undefined" && isLocalDevelopmentEnvironment()) {
    set(window, "fromStore", () => {
        return store.getState()
    })
}

// see rtk-query: https://redux-toolkit.js.org/rtk-query/overview#configure-the-store
// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch)
addReporter(() => `Redux Store State: ${JSON.stringify(store.getState(), null, "  ")}`)

export { store }
export default store
